import { openWindow } from '@/common';
import { newBroadcastChannel } from '../../../../messaging';
import api from '../../../../API';

interface SecurityContext {
  state: string,
  codeVerifier: string|null
}

export interface TokenResponse {
  /* eslint-disable camelcase */
  token_type: string,
  access_token: string,
  expires_in: number|null,
  refresh_token: string|null,
  refresh_token_expires_in: number|null
  /* eslint-enable camelcase */
}

export interface SalesforceTokenResponse extends TokenResponse {
  /* eslint-disable camelcase */
  instance_url: string,
  /* eslint-enable camelcase */
}

export class OAuth2CallbackSuccessful {
  static eventName = 'oauth2.callback_successful';

  eventName: string;

  platform: string;

  response: TokenResponse

  constructor(platform: string, response: TokenResponse) {
    this.eventName = OAuth2CallbackSuccessful.eventName;
    this.platform = platform;
    this.response = response;
  }
}

async function createSecurityContext(): Promise<SecurityContext> {
  const res = await api.post('oauth2/security-context');
  return res.data;
}

const securityContextKey = 'oauth2SecurityContext';
function saveSecurityContext(context: SecurityContext): void {
  window.sessionStorage.setItem(securityContextKey, JSON.stringify(context));
}

function getSecurityContext(): SecurityContext | null {
  const securityContext = window.sessionStorage.getItem(securityContextKey);
  if (securityContext === null) return null;
  return JSON.parse(securityContext);
}

export async function requestToken(platform: string, scope: string): Promise<TokenResponse|null> {
  const securityContext = await createSecurityContext();
  saveSecurityContext(securityContext);
  const queryParams = new URLSearchParams({ scope, state: securityContext.state });
  if (securityContext.codeVerifier !== null) {
    queryParams.set('codeVerifier', securityContext.codeVerifier);
  }

  return new Promise((resolve) => {
    let bc: BroadcastChannel|null = newBroadcastChannel();
    bc.addEventListener('message', (event) => {
      if (!bc) return;
      if (typeof event.data.platform === 'undefined') return;
      if (event.data.platform !== platform) return;
      if (event.data.eventName !== OAuth2CallbackSuccessful.eventName) return;
      bc.close();
      bc = null;
      resolve((event.data as OAuth2CallbackSuccessful).response);
    });
    const windowOpts = {
      onClose(): void {
        if (bc) resolve(null);
      },
    };
    openWindow(`${window.env.IAM.EXTERNAL.URL}/platforms/${platform}/oauth2/login?${queryParams.toString()}`, windowOpts);
  });
}

export async function callback(platform: string, params: Record<string, string>): Promise<void> {
  const securityContext = getSecurityContext();
  if (securityContext === null) {
    throw new Error('No OAuth2 security context found. Please retry the login process.');
  }
  if (!params.state || params.state !== securityContext.state) {
    throw new Error('OAuth2 state is invalid.');
  }

  const url = `platforms/${platform}/oauth2/callback`;
  const res = await api.get(
    url,
    { params: { ...params, codeVerifier: securityContext.codeVerifier } },
  );
  const bc = newBroadcastChannel();
  bc.postMessage(new OAuth2CallbackSuccessful(platform, res.data));
  bc.close();
  window.close();
}
